import { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Trash2, ChevronLeft, ChevronRight, Save, Check, Grid3X3 } from 'lucide-react';
import { toast } from 'sonner';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PREDEFINED_FIELDS = [
  { name: 'seller_name', label: "Seller's Name", map_to: 'seller_name' },
  { name: 'property_address', label: 'Property Address', map_to: 'property_address' },
  { name: 'folio', label: 'Folio Number', map_to: 'folio' },
  { name: 'county', label: 'County', map_to: 'county' },
  { name: 'legal_description', label: 'Legal Description', map_to: 'legal_description' },
  { name: 'offer_amount', label: 'Offer Amount', map_to: 'offer_amount' },
  { name: 'escrow_deposit', label: 'Escrow Deposit', map_to: 'escrow_deposit' },
  { name: 'closing_date', label: 'Closing Date', map_to: 'closing_date' },
  { name: 'inspection_time', label: 'Inspection Time', map_to: 'inspection_time' },
  { name: 'assignability', label: 'Assignability', map_to: 'assignability' },
  { name: 'closing_costs', label: 'Closing Costs', map_to: 'closing_costs' },
  { name: 'additional_terms', label: 'Additional Terms', map_to: 'additional_terms' },
  { name: 'agent_name', label: 'Listing Agent Name', map_to: 'agent_name' },
  { name: 'listing_broker', label: 'Listing Broker', map_to: 'listing_broker' },
  { name: 'agent_email', label: 'Listing Agent Email', map_to: 'agent_email' },
  { name: 'date_signed', label: 'Date Signed', map_to: 'date_signed' },
  { name: 'custom_text', label: 'Custom Text', map_to: '' },
];

export default function PdfFieldEditor({ templateId, onClose, onSaved }) {
  const [pdfData, setPdfData] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [canvasSize, setCanvasSize] = useState(null);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const GRID_STEP = 2; // snap every 2%
  const pageContainerRef = useRef(null);
  const dragStartRef = useRef(null);
  const hasDraggedRef = useRef(false);

  useEffect(() => {
    API.get(`/templates/${templateId}`).then(res => {
      setPdfData(res.data.pdf_data);
      const normalizedFields = (res.data.fields || []).map((f, i) => ({
        name: f.name || `field_${i + 1}`,
        label: f.label || f.name || `Field ${i + 1}`,
        field_type: f.field_type || 'text',
        map_to: f.map_to || '',
        page_num: f.page_num ?? 0,
        x_percent: f.x_percent ?? 10,
        y_percent: f.y_percent ?? (10 + i * 5),
        font_size: f.font_size ?? 10,
        w_percent: f.w_percent ?? 0,
        h_percent: f.h_percent ?? 0,
      }));
      setFields(normalizedFields);
      setTemplateName(res.data.name);
      setLoading(false);
    }).catch(() => {
      toast.error('Failed to load template');
      onClose();
    });
  }, [templateId, onClose]);

  const handleDocLoad = ({ numPages: n }) => setNumPages(n);

  // Measure the actual canvas element after it renders
  const measureCanvas = useCallback(() => {
    const container = pageContainerRef.current;
    if (!container) return;
    const canvas = container.querySelector('canvas');
    if (!canvas) return;
    const containerRect = container.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    setCanvasSize({
      width: canvasRect.width,
      height: canvasRect.height,
      offsetLeft: canvasRect.left - containerRect.left,
      offsetTop: canvasRect.top - containerRect.top,
    });
  }, []);

  const handlePageRenderSuccess = useCallback(() => {
    requestAnimationFrame(measureCanvas);
  }, [measureCanvas]);

  // Re-measure on window resize
  useEffect(() => {
    window.addEventListener('resize', measureCanvas);
    return () => window.removeEventListener('resize', measureCanvas);
  }, [measureCanvas]);

  // Add a predefined field to the current page
  const addPredefinedField = useCallback((predef) => {
    const existingCount = fields.filter(f => f.name === predef.name).length;
    const suffix = existingCount > 0 ? `_${existingCount + 1}` : '';
    const idx = fields.length;
    const newField = {
      name: `${predef.name}${suffix}`,
      label: predef.label,
      field_type: 'text',
      map_to: predef.map_to,
      page_num: currentPage - 1,
      x_percent: 20 + (idx % 3) * 20,
      y_percent: 15 + (idx % 5) * 10,
      font_size: 10,
      w_percent: 0,
      h_percent: 0,
    };
    setFields(prev => [...prev, newField]);
    setSelectedIdx(idx);
    toast.success(`Added "${predef.label}" — drag it into position`);
  }, [currentPage, fields]);

  // Snap helper
  const snap = useCallback((val) => {
    if (!snapToGrid) return Math.round(val * 10) / 10;
    return Math.round(val / GRID_STEP) * GRID_STEP;
  }, [snapToGrid, GRID_STEP]);

  // Drag handlers — use canvas dimensions for precise mapping
  const handleFieldMouseDown = useCallback((e, globalIdx) => {
    e.stopPropagation();
    e.preventDefault();
    if (!canvasSize) return;
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startXPct: fields[globalIdx].x_percent,
      startYPct: fields[globalIdx].y_percent,
      canvasWidth: canvasSize.width,
      canvasHeight: canvasSize.height,
    };
    hasDraggedRef.current = false;
    setDraggingIdx(globalIdx);
    setSelectedIdx(globalIdx);
  }, [fields, canvasSize]);

  useEffect(() => {
    if (draggingIdx === null) return;
    const handleMouseMove = (e) => {
      if (!dragStartRef.current) return;
      const dx = e.clientX - dragStartRef.current.mouseX;
      const dy = e.clientY - dragStartRef.current.mouseY;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasDraggedRef.current = true;
      const dxPct = (dx / dragStartRef.current.canvasWidth) * 100;
      const dyPct = (dy / dragStartRef.current.canvasHeight) * 100;
      const newX = Math.max(0, Math.min(98, dragStartRef.current.startXPct + dxPct));
      const newY = Math.max(0, Math.min(98, dragStartRef.current.startYPct + dyPct));
      setFields(prev => prev.map((f, i) =>
        i === draggingIdx ? { ...f, x_percent: snap(newX), y_percent: snap(newY) } : f
      ));
    };
    const handleMouseUp = () => { setDraggingIdx(null); dragStartRef.current = null; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [draggingIdx, snap]);

  const removeField = (idx) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
    if (selectedIdx === idx) setSelectedIdx(null);
    else if (selectedIdx > idx) setSelectedIdx(prev => prev - 1);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put(`/templates/${templateId}/fields`, { fields });
      toast.success('Fields saved');
      onSaved?.();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const pageFields = fields.filter(f => f.page_num === currentPage - 1);
  const placedFieldNames = new Set(fields.map(f => f.name));

  // Convert field x_percent/y_percent to pixel position relative to the container
  const getFieldPixelPos = (field) => {
    if (!canvasSize) return { left: 0, top: 0 };
    return {
      left: canvasSize.offsetLeft + (field.x_percent / 100) * canvasSize.width,
      top: canvasSize.offsetTop + (field.y_percent / 100) * canvasSize.height,
    };
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-[#0073ea] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="pdf-field-editor">
      {/* Header */}
      <div className="h-14 border-b border-gray-200 flex items-center justify-between px-4 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-gray-900 h-8 w-8" data-testid="close-field-editor">
            <X className="h-5 w-5" />
          </Button>
          <h2 className="text-base font-semibold text-gray-900">{templateName}</h2>
          <Badge variant="outline" className="border-gray-300 text-gray-500 text-xs">
            {fields.length} fields placed
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="toggle-grid-btn"
            variant="ghost"
            size="sm"
            onClick={() => setSnapToGrid(prev => !prev)}
            className={`h-8 px-3 text-xs ${snapToGrid ? 'bg-[#cce5ff] text-[#0073ea] border border-[#0073ea]/30' : 'text-gray-500 border border-gray-300'}`}
          >
            <Grid3X3 className="h-3.5 w-3.5 mr-1.5" />
            {snapToGrid ? 'Grid On' : 'Grid Off'}
          </Button>
          <Button data-testid="save-fields-btn" onClick={handleSave} disabled={saving} size="sm" className="bg-[#0073ea] hover:bg-[#0060c2] text-white">
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? 'Saving...' : 'Save Fields'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* PDF Viewer */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#f6f7fb]">
          <div className="h-10 flex items-center justify-center gap-3 border-b border-gray-200 bg-white shrink-0">
            <Button variant="ghost" size="icon" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} className="h-7 w-7 text-gray-400">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600">Page {currentPage} of {numPages}</span>
            <Button variant="ghost" size="icon" disabled={currentPage >= numPages} onClick={() => setCurrentPage(p => p + 1)} className="h-7 w-7 text-gray-400">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-auto flex justify-center p-6 bg-[#f6f7fb]">
            <div ref={pageContainerRef} className="relative inline-block shadow-2xl">
              {pdfData && (
                <Document file={`data:application/pdf;base64,${pdfData}`} onLoadSuccess={handleDocLoad} loading={null}>
                  <Page
                    pageNumber={currentPage}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={680}
                    onRenderSuccess={handlePageRenderSuccess}
                  />
                </Document>
              )}

              {/* Grid overlay */}
              {snapToGrid && canvasSize && (
                <svg
                  className="absolute pointer-events-none z-[5]"
                  style={{
                    left: `${canvasSize.offsetLeft}px`,
                    top: `${canvasSize.offsetTop}px`,
                    width: `${canvasSize.width}px`,
                    height: `${canvasSize.height}px`,
                  }}
                >
                  {Array.from({ length: Math.floor(100 / GRID_STEP) - 1 }, (_, i) => {
                    const pct = (i + 1) * GRID_STEP;
                    const x = (pct / 100) * canvasSize.width;
                    const y = (pct / 100) * canvasSize.height;
                    const isMajor = pct % 10 === 0;
                    return (
                      <g key={i}>
                        <line x1={x} y1={0} x2={x} y2={canvasSize.height} stroke={isMajor ? 'rgba(56,189,248,0.2)' : 'rgba(56,189,248,0.07)'} strokeWidth={isMajor ? 0.8 : 0.4} />
                        <line x1={0} y1={y} x2={canvasSize.width} y2={y} stroke={isMajor ? 'rgba(56,189,248,0.2)' : 'rgba(56,189,248,0.07)'} strokeWidth={isMajor ? 0.8 : 0.4} />
                      </g>
                    );
                  })}
                </svg>
              )}

              {/* Draggable field markers — positioned via pixel coords relative to the actual canvas */}
              {canvasSize && pageFields.map((field) => {
                const globalIdx = fields.indexOf(field);
                const isSelected = globalIdx === selectedIdx;
                const isDragging = globalIdx === draggingIdx;
                const fontSize = Math.max(8, Math.min(18, (field.font_size || 10) * 0.9));
                const pos = getFieldPixelPos(field);
                const hasSize = field.w_percent > 0 && field.h_percent > 0;
                const wPx = hasSize ? (field.w_percent / 100) * canvasSize.width : undefined;
                const hPx = hasSize ? (field.h_percent / 100) * canvasSize.height : undefined;
                return (
                  <div
                    key={globalIdx}
                    data-testid={`field-marker-${globalIdx}`}
                    className={`absolute flex items-start gap-1 rounded select-none cursor-grab
                      ${isDragging ? 'opacity-90 z-30 shadow-lg shadow-[#0073ea]/40 scale-105 !cursor-grabbing' : ''}
                      ${!isDragging ? 'transition-[box-shadow,transform] duration-150' : ''}
                      ${isSelected && !isDragging
                        ? 'bg-[#0073ea]/30 text-[#0073ea] ring-2 ring-[#0073ea]/60 ring-offset-1 ring-offset-transparent z-20 border border-[#0073ea]'
                        : 'bg-[#0073ea]/20 text-[#0073ea] hover:bg-[#0073ea]/30 z-10 border border-[#0073ea]/50'
                      }`}
                    style={{
                      left: `${pos.left}px`,
                      top: `${pos.top}px`,
                      fontSize: `${fontSize}px`,
                      padding: `${Math.max(1, fontSize * 0.15)}px ${Math.max(3, fontSize * 0.4)}px`,
                      lineHeight: 1.3,
                      ...(hasSize ? { width: `${wPx}px`, height: `${hPx}px`, whiteSpace: 'normal', overflow: 'hidden' } : { whiteSpace: 'nowrap' }),
                    }}
                    onMouseDown={(e) => handleFieldMouseDown(e, globalIdx)}
                    onClick={(e) => { e.stopPropagation(); if (!hasDraggedRef.current) setSelectedIdx(globalIdx); }}
                  >
                    {field.label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-72 border-l border-gray-200 bg-white flex flex-col shrink-0">
          {/* Field list to add */}
          <div className="p-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Contract Fields</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Click to add, then drag on PDF</p>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-0.5">
              {PREDEFINED_FIELDS.map((predef) => {
                const isPlaced = placedFieldNames.has(predef.name);
                return (
                  <button
                    key={predef.name}
                    data-testid={`add-${predef.name}`}
                    onClick={() => addPredefinedField(predef)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-left text-sm transition-all duration-150 ${
                      isPlaced
                        ? 'text-gray-400 bg-gray-50'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-[#0073ea]'
                    }`}
                  >
                    <span className="truncate">{predef.label}</span>
                    {isPlaced ? (
                      <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 ml-2" />
                    ) : (
                      <Plus className="h-3.5 w-3.5 text-gray-400 shrink-0 ml-2" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Placed fields on current page */}
            {pageFields.length > 0 && (
              <div className="p-2 border-t border-gray-200 mt-2">
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider px-2 py-1">
                  On this page ({pageFields.length})
                </p>
                <div className="space-y-0.5">
                  {pageFields.map((field) => {
                    const globalIdx = fields.indexOf(field);
                    const isSelected = globalIdx === selectedIdx;
                    return (
                      <div
                        key={globalIdx}
                        data-testid={`placed-field-${globalIdx}`}
                        onClick={() => setSelectedIdx(globalIdx)}
                        className={`flex items-center justify-between px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-150 ${
                          isSelected ? 'bg-blue-50 text-[#0073ea]' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="truncate text-xs">{field.label}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-red-400 hover:bg-red-50 shrink-0"
                          onClick={(e) => { e.stopPropagation(); removeField(globalIdx); }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </ScrollArea>

          {/* Selected field controls */}
          {selectedIdx !== null && fields[selectedIdx] && (
            <div className="p-3 border-t border-gray-200 bg-[#f6f7fb] space-y-3">
              <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{fields[selectedIdx].label}</p>
              {/* Font size */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14">Font size</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                    onClick={() => { const c = fields[selectedIdx].font_size || 10; setFields(p => p.map((f, i) => i === selectedIdx ? { ...f, font_size: Math.max(6, c - 1) } : f)); }}>
                    <span className="text-sm font-bold">-</span>
                  </Button>
                  <span className="text-sm text-gray-900 font-mono w-8 text-center">{fields[selectedIdx].font_size || 10}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                    onClick={() => { const c = fields[selectedIdx].font_size || 10; setFields(p => p.map((f, i) => i === selectedIdx ? { ...f, font_size: Math.min(48, c + 1) } : f)); }}>
                    <span className="text-sm font-bold">+</span>
                  </Button>
                  <span className="text-[10px] text-gray-400 ml-1">pt</span>
                </div>
              </div>
              {/* Width */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14">Width %</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                    onClick={() => { const c = fields[selectedIdx].w_percent || 0; setFields(p => p.map((f, i) => i === selectedIdx ? { ...f, w_percent: Math.max(0, c - 2) } : f)); }}>
                    <span className="text-sm font-bold">-</span>
                  </Button>
                  <span className="text-sm text-gray-900 font-mono w-8 text-center">{fields[selectedIdx].w_percent || 0}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                    onClick={() => { const c = fields[selectedIdx].w_percent || 0; setFields(p => p.map((f, i) => i === selectedIdx ? { ...f, w_percent: Math.min(80, c + 2) } : f)); }}>
                    <span className="text-sm font-bold">+</span>
                  </Button>
                </div>
              </div>
              {/* Height */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14">Height %</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                    onClick={() => { const c = fields[selectedIdx].h_percent || 0; setFields(p => p.map((f, i) => i === selectedIdx ? { ...f, h_percent: Math.max(0, c - 2) } : f)); }}>
                    <span className="text-sm font-bold">-</span>
                  </Button>
                  <span className="text-sm text-gray-900 font-mono w-8 text-center">{fields[selectedIdx].h_percent || 0}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                    onClick={() => { const c = fields[selectedIdx].h_percent || 0; setFields(p => p.map((f, i) => i === selectedIdx ? { ...f, h_percent: Math.min(80, c + 2) } : f)); }}>
                    <span className="text-sm font-bold">+</span>
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-gray-400">Set Width &amp; Height &gt; 0 for multi-line text box</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
